import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE } from '../apiConfig';
import '../css/petrotalent.css';

const PetroTalent = () => {
    const [stats, setStats] = useState(null);
    const [results, setResults] = useState(null);
    const [filterOpts, setFilterOpts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [navSolid, setNavSolid] = useState(false);

    // Request modal
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestSubmitting, setRequestSubmitting] = useState(false);
    const [requestSuccess, setRequestSuccess] = useState(false);
    const [requestError, setRequestError] = useState(null);
    const emptyPosition = { position: '', headcount: 1, min_experience: '', max_experience: '', min_age: '', max_age: '', nationality_preference: '', certificates: '', educational_degree: '' };
    const [reqForm, setReqForm] = useState({
        company_name: '', contact_name: '', contact_email: '', contact_phone: '', country: '', industry: '', notes: '',
        positions: [{ ...emptyPosition }],
    });

    // Search filters
    const [position, setPosition] = useState('');
    const [minExp, setMinExp] = useState('');
    const [maxExp, setMaxExp] = useState('');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [nationality, setNationality] = useState('');
    const [countries, setCountries] = useState('');
    const [iwcf, setIwcf] = useState(false);
    const [iadc, setIadc] = useState(false);
    const [degree, setDegree] = useState('');

    // Force-hide site header/footer via body class
    useEffect(() => {
        document.body.classList.add('pt-standalone');
        return () => document.body.classList.remove('pt-standalone');
    }, []);

    // Scroll for nav background
    useEffect(() => {
        const fn = () => setNavSolid(window.scrollY > 50);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    // Load stats + filter options (independently, so one failing doesn't break the other)
    useEffect(() => {
        // Fetch stats (required)
        fetch(`${API_BASE}/petro-talent/stats`)
            .then(r => { if (!r.ok) throw new Error('Stats API error'); return r.json(); })
            .then(statsRes => {
                if (statsRes.success) {
                    setStats(statsRes.data);
                    // Build dropdown options from stats if filter-options isn't available
                    setFilterOpts(prev => prev || {
                        positions: Object.keys(statsRes.data.positions || {}),
                        nationalities: Object.keys(statsRes.data.nationalities || {}),
                        education: [],
                        countries: [],
                    });
                } else {
                    setError(statsRes.message || 'Failed to load stats');
                }
                setLoading(false);
            })
            .catch(() => {
                setError('Unable to connect to talent database');
                setLoading(false);
            });

        // Fetch filter options (optional — silently fails if endpoint not deployed yet)
        fetch(`${API_BASE}/petro-talent/filter-options`)
            .then(r => { if (!r.ok) throw new Error('not available'); return r.json(); })
            .then(filtersRes => {
                if (filtersRes.success) setFilterOpts(filtersRes.data);
            })
            .catch(() => { /* fallback: dropdown options derived from stats */ });
    }, []);

    const doSearch = useCallback((e) => {
        e.preventDefault();
        setSearching(true);
        setError(null);
        const body = {};
        if (position) body.position = position;
        if (minExp) body.min_experience = parseInt(minExp);
        if (maxExp) body.max_experience = parseInt(maxExp);
        if (minAge) body.min_age = parseInt(minAge);
        if (maxAge) body.max_age = parseInt(maxAge);
        if (nationality) body.nationality = nationality;
        if (countries) body.countries = countries;
        if (iwcf) body.iwcf = true;
        if (iadc) body.iadc = true;
        if (degree) body.educational_degree = degree;

        fetch(`${API_BASE}/petro-talent/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        })
            .then(r => { if (!r.ok) throw new Error('Search failed'); return r.json(); })
            .then(d => { if (d.success) setResults(d.data); else setError(d.message); setSearching(false); })
            .catch(err => { setError(err.message); setSearching(false); });
    }, [position, minExp, maxExp, minAge, maxAge, nationality, countries, iwcf, iadc, degree]);

    const reset = () => {
        setPosition(''); setMinExp(''); setMaxExp(''); setMinAge(''); setMaxAge('');
        setNationality(''); setCountries(''); setIwcf(false); setIadc(false); setDegree('');
        setResults(null);
    };

    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

    // Request modal helpers
    const updateReqField = (field, value) => setReqForm(prev => ({ ...prev, [field]: value }));
    const updatePosition = (idx, field, value) => {
        setReqForm(prev => {
            const positions = [...prev.positions];
            positions[idx] = { ...positions[idx], [field]: value };
            return { ...prev, positions };
        });
    };
    const addPosition = () => setReqForm(prev => ({ ...prev, positions: [...prev.positions, { ...emptyPosition }] }));
    const removePosition = (idx) => setReqForm(prev => ({ ...prev, positions: prev.positions.filter((_, i) => i !== idx) }));

    const submitRequest = async () => {
        setRequestSubmitting(true);
        setRequestError(null);
        try {
            const body = {
                ...reqForm,
                positions: reqForm.positions.map(p => ({
                    ...p,
                    headcount: parseInt(p.headcount) || 1,
                    min_experience: p.min_experience ? parseInt(p.min_experience) : null,
                    max_experience: p.max_experience ? parseInt(p.max_experience) : null,
                    min_age: p.min_age ? parseInt(p.min_age) : null,
                    max_age: p.max_age ? parseInt(p.max_age) : null,
                })),
            };
            const res = await fetch(`${API_BASE}/petro-talent/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (data.success) {
                setRequestSuccess(true);
            } else {
                setRequestError(data.message || 'Failed to submit request.');
            }
        } catch (err) {
            setRequestError('Network error. Please try again.');
        }
        setRequestSubmitting(false);
    };

    const openRequestModal = () => {
        setShowRequestModal(true);
        setRequestSuccess(false);
        setRequestError(null);
        setReqForm({ company_name: '', contact_name: '', contact_email: '', contact_phone: '', country: '', industry: '', notes: '', positions: [{ ...emptyPosition }] });
    };

    const BarChart = ({ data, color = 'bf-indigo' }) => {
        if (!data || Object.keys(data).length === 0)
            return <p style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '16px 0' }}>No data available</p>;
        const max = Math.max(...Object.values(data));
        return Object.entries(data).map(([k, v]) => (
            <div className="pt-bar" key={k}>
                <div className="pt-bar-head">
                    <span className="pt-bar-name">{k}</span>
                    <span className="pt-bar-num">{v}</span>
                </div>
                <div className="pt-bar-track">
                    <div className={`pt-bar-fill ${color}`} style={{ width: max > 0 ? `${(v / max) * 100}%` : '0%' }} />
                </div>
            </div>
        ));
    };

    const d = results || stats;

    return (
        <div className="pt-app">
            {/* Background */}
            <div className="pt-bg">
                <div className="pt-bg-mesh" />
                <div className="pt-bg-grid" />
                <div className="pt-orb pt-orb-1" />
                <div className="pt-orb pt-orb-2" />
                <div className="pt-orb pt-orb-3" />
            </div>

            <div className="pt-content">
                {/* ─── Navbar ─── */}
                <nav className={`pt-nav ${navSolid ? 'solid' : ''}`}>
                    <div className="pt-container">
                        <div className="pt-nav-inner">
                            <Link to="/services/petro-talent" className="pt-logo">
                                <div className="pt-logo-mark">PT</div>
                                <span className="pt-logo-text">PetroTalent</span>
                            </Link>
                            <ul className="pt-nav-menu">
                                <li><Link to="/">PetroChoice</Link></li>
                                <li><Link to="/careers">Careers</Link></li>
                                <li><a onClick={() => scrollTo('search')}>Search</a></li>
                            </ul>
                            <button className="pt-nav-btn" onClick={() => scrollTo('search')}>
                                <i className="fas fa-search" /> Search Now
                            </button>
                            <button className="pt-nav-btn pt-nav-btn-request" onClick={openRequestModal}>
                                <i className="fas fa-paper-plane" /> Request Talent
                            </button>
                        </div>
                    </div>
                </nav>

                {/* ─── Hero ─── */}
                <section className="pt-hero">
                    <div className="pt-container">
                        <div className="pt-badge">
                            <span className="pt-badge-dot" />
                            Talent Intelligence Platform
                        </div>
                        <h1 className="pt-hero-title">
                            Find Your Next<br />
                            <span className="pt-grad">Oil & Gas Expert</span>
                        </h1>
                        <p className="pt-hero-sub">
                            Search across <strong>{stats ? stats.total_applicants?.toLocaleString() : '...'}</strong> qualified
                            professionals. Get instant analytics on available talent — no personal data, just insights.
                        </p>
                        {stats && (
                            <div className="pt-metrics">
                                <div className="pt-metric">
                                    <div className="pt-metric-num">{stats.total_applicants?.toLocaleString()}</div>
                                    <div className="pt-metric-label">Profiles</div>
                                </div>
                                <div className="pt-metric">
                                    <div className="pt-metric-num">{Object.keys(stats.positions || {}).length}</div>
                                    <div className="pt-metric-label">Positions</div>
                                </div>
                                <div className="pt-metric">
                                    <div className="pt-metric-num">{Object.keys(stats.nationalities || {}).length}</div>
                                    <div className="pt-metric-label">Nationalities</div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* ─── Search ─── */}
                <section className="pt-search" id="search">
                    <div className="pt-container">
                        <div className="pt-card">
                            <div className="pt-card-head">
                                <div className="pt-card-dot" />
                                <span className="pt-card-title">Search Talent Pool</span>
                            </div>
                            <form onSubmit={doSearch}>
                                <div className="pt-grid pt-grid-3">
                                    <div className="pt-field">
                                        <label className="pt-label">Position / Job Title</label>
                                        <select className="pt-select" value={position} onChange={e => setPosition(e.target.value)}>
                                            <option value="">All Positions</option>
                                            {filterOpts?.positions?.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-field">
                                        <label className="pt-label">Min Experience</label>
                                        <select className="pt-select" value={minExp} onChange={e => setMinExp(e.target.value)}>
                                            <option value="">Any</option>
                                            {[0, 1, 2, 3, 5, 7, 10, 15, 20, 25].map(y => <option key={y} value={y}>{y} years</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-field">
                                        <label className="pt-label">Max Experience</label>
                                        <select className="pt-select" value={maxExp} onChange={e => setMaxExp(e.target.value)}>
                                            <option value="">Any</option>
                                            {[2, 5, 10, 15, 20, 25, 30, 35, 40].map(y => <option key={y} value={y}>{y} years</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-field">
                                        <label className="pt-label">Nationality</label>
                                        <select className="pt-select" value={nationality} onChange={e => setNationality(e.target.value)}>
                                            <option value="">All Nationalities</option>
                                            {filterOpts?.nationalities?.map(n => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-field">
                                        <label className="pt-label">Min Age</label>
                                        <select className="pt-select" value={minAge} onChange={e => setMinAge(e.target.value)}>
                                            <option value="">Any</option>
                                            {[20, 25, 30, 35, 40, 45, 50, 55, 60].map(a => <option key={a} value={a}>{a} years</option>)}
                                        </select>
                                    </div>
                                    <div className="pt-field">
                                        <label className="pt-label">Max Age</label>
                                        <select className="pt-select" value={maxAge} onChange={e => setMaxAge(e.target.value)}>
                                            <option value="">Any</option>
                                            {[25, 30, 35, 40, 45, 50, 55, 60, 65].map(a => <option key={a} value={a}>{a} years</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ marginTop: 20 }}>
                                    <div className="pt-field">
                                        <label className="pt-label">Certificates</label>
                                        <div className="pt-checks">
                                            <label className="pt-check">
                                                <input type="checkbox" className="pt-checkbox" checked={iwcf} onChange={e => setIwcf(e.target.checked)} />
                                                <span className="pt-check-label">IWCF</span>
                                            </label>
                                            <label className="pt-check">
                                                <input type="checkbox" className="pt-checkbox" checked={iadc} onChange={e => setIadc(e.target.checked)} />
                                                <span className="pt-check-label">IADC</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="pt-actions">
                                    <button type="submit" className="pt-btn pt-btn-primary" disabled={searching}>
                                        {searching
                                            ? <><span className="pt-spinner" style={{ width: 18, height: 18, borderWidth: 2, display: 'inline-block' }} /> Searching...</>
                                            : <><i className="fas fa-search" /> Search Talent</>}
                                    </button>
                                    <button type="button" className="pt-btn pt-btn-ghost" onClick={reset}>
                                        <i className="fas fa-undo" /> Reset All
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </section>

                {/* Loading */}
                {loading && (
                    <div className="pt-loading">
                        <div className="pt-spinner" />
                        <p>Connecting to talent database...</p>
                    </div>
                )}

                {/* Error */}
                {error && (
                    <div className="pt-container">
                        <div className="pt-error"><i className="fas fa-exclamation-triangle" /> {error}</div>
                    </div>
                )}

                {/* ─── Results ─── */}
                {d && !loading && (
                    <>
                        {results && (
                            <div className="pt-container pt-results-intro">
                                <h3 className="pt-results-title">
                                    <i className="fas fa-chart-bar" />Search Results
                                </h3>
                                <p className="pt-results-desc">
                                    Found <strong>{results.total_matches?.toLocaleString()}</strong> candidate{results.total_matches !== 1 ? 's' : ''} matching your criteria
                                </p>
                            </div>
                        )}

                        {(!results || results.total_matches > 0) && (
                            <>
                                <section className="pt-stats">
                                    <div className="pt-container">
                                        <div className="pt-stats-strip">
                                            <div className="pt-stat">
                                                <div className="pt-stat-glow sg-indigo" />
                                                <div className="pt-stat-icon si-indigo"><i className="fas fa-users" /></div>
                                                <div className="pt-stat-val">{(results ? results.total_matches : stats?.total_applicants)?.toLocaleString() || '0'}</div>
                                                <div className="pt-stat-lbl">{results ? 'Matches' : 'Total Profiles'}</div>
                                            </div>
                                            <div className="pt-stat">
                                                <div className="pt-stat-glow sg-emerald" />
                                                <div className="pt-stat-icon si-emerald"><i className="fas fa-user-clock" /></div>
                                                <div className="pt-stat-val">{(results ? results.age_stats?.average : stats?.average_age) || 'N/A'}</div>
                                                <div className="pt-stat-lbl">Avg Age</div>
                                            </div>
                                            <div className="pt-stat">
                                                <div className="pt-stat-glow sg-amber" />
                                                <div className="pt-stat-icon si-amber"><i className="fas fa-briefcase" /></div>
                                                <div className="pt-stat-val">{results?.experience_stats?.average_years || 'N/A'}</div>
                                                <div className="pt-stat-lbl">Avg Experience</div>
                                            </div>
                                            {results?.certificates && (
                                                <>
                                                    <div className="pt-stat">
                                                        <div className="pt-stat-glow sg-violet" />
                                                        <div className="pt-stat-icon si-violet"><i className="fas fa-certificate" /></div>
                                                        <div className="pt-stat-val">{results.certificates.iwcf}</div>
                                                        <div className="pt-stat-lbl">IWCF</div>
                                                    </div>
                                                    <div className="pt-stat">
                                                        <div className="pt-stat-glow sg-rose" />
                                                        <div className="pt-stat-icon si-rose"><i className="fas fa-award" /></div>
                                                        <div className="pt-stat-val">{results.certificates.iadc}</div>
                                                        <div className="pt-stat-lbl">IADC</div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <section className="pt-panels">
                                    <div className="pt-container">
                                        <div className="pt-row">
                                            <div className="pt-col pt-col-4">
                                                <div className="pt-panel pt-fade">
                                                    <div className="pt-panel-title"><i className="fas fa-chart-bar" /> Experience Distribution</div>
                                                    <BarChart data={results ? results.experience_stats?.distribution : stats?.experience_distribution} color="bf-indigo" />
                                                </div>
                                            </div>
                                            <div className="pt-col pt-col-4">
                                                <div className="pt-panel pt-fade" style={{ animationDelay: '0.08s' }}>
                                                    <div className="pt-panel-title"><i className="fas fa-globe" /> Top Nationalities</div>
                                                    <BarChart data={results ? results.nationalities : stats?.nationalities} color="bf-emerald" />
                                                </div>
                                            </div>
                                            <div className="pt-col pt-col-4">
                                                <div className="pt-panel pt-fade" style={{ animationDelay: '0.16s' }}>
                                                    <div className="pt-panel-title"><i className="fas fa-id-badge" /> Top Positions</div>
                                                    <BarChart data={results ? results.positions : stats?.positions} color="bf-violet" />
                                                </div>
                                            </div>

                                            {results?.education && Object.keys(results.education).length > 0 && (
                                                <div className="pt-col pt-col-4">
                                                    <div className="pt-panel pt-fade" style={{ animationDelay: '0.08s' }}>
                                                        <div className="pt-panel-title"><i className="fas fa-graduation-cap" /> Education</div>
                                                        <BarChart data={results.education} color="bf-cyan" />
                                                    </div>
                                                </div>
                                            )}
                                            {results?.age_stats && results.age_stats.min !== null && (
                                                <div className="pt-col pt-col-4">
                                                    <div className="pt-panel pt-fade" style={{ animationDelay: '0.16s' }}>
                                                        <div className="pt-panel-title"><i className="fas fa-id-card" /> Age Range</div>
                                                        <div className="pt-trio">
                                                            <div className="pt-trio-box"><div className="pt-trio-num">{results.age_stats.min}</div><div className="pt-trio-lbl">Youngest</div></div>
                                                            <div className="pt-trio-box"><div className="pt-trio-num">{results.age_stats.average}</div><div className="pt-trio-lbl">Average</div></div>
                                                            <div className="pt-trio-box"><div className="pt-trio-num">{results.age_stats.max}</div><div className="pt-trio-lbl">Oldest</div></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            </>
                        )}

                        {results && results.total_matches === 0 && (
                            <div className="pt-empty">
                                <div className="pt-empty-icon"><i className="fas fa-search" /></div>
                                <h4>No Candidates Found</h4>
                                <p>Try adjusting your search criteria to find matching talent</p>
                            </div>
                        )}
                    </>
                )}

                {/* ─── Footer ─── */}
                <footer className="pt-footer">
                    <div className="pt-container">
                        <div className="pt-footer-inner">
                            <div className="pt-footer-copy">
                                © {new Date().getFullYear()} PetroTalent — A <Link to="/">PetroChoice</Link> Product
                            </div>
                            <div className="pt-footer-links">
                                <Link to="/">Home</Link>
                                <Link to="/careers">Careers</Link>
                                <Link to="/contact">Contact</Link>
                                <Link to="/privacy">Privacy</Link>
                            </div>
                        </div>
                    </div>
                </footer>

                {/* ─── Request Modal ─── */}
                {showRequestModal && (
                    <div className="ptr-overlay" onClick={() => setShowRequestModal(false)}>
                        <div className="ptr-modal" onClick={e => e.stopPropagation()}>
                            <div className="ptr-header">
                                <div className="ptr-header-left">
                                    <div className="ptr-header-icon"><i className="fas fa-paper-plane" /></div>
                                    <div>
                                        <h2 className="ptr-title">Request Talent</h2>
                                        <p className="ptr-subtitle">Tell us what you need — our team will get back to you shortly</p>
                                    </div>
                                </div>
                                <button className="ptr-close" onClick={() => setShowRequestModal(false)}><i className="fas fa-times" /></button>
                            </div>

                            {requestSuccess ? (
                                <div className="ptr-success">
                                    <div className="ptr-success-icon"><i className="fas fa-check-circle" /></div>
                                    <h3>Request Submitted!</h3>
                                    <p>Thank you for your interest. Our talent team will review your request and contact you within 24–48 hours.</p>
                                    <button className="pt-btn pt-btn-primary" onClick={() => setShowRequestModal(false)}>Close</button>
                                </div>
                            ) : (
                                <div className="ptr-body">
                                    {requestError && <div className="ptr-error"><i className="fas fa-exclamation-triangle" /> {requestError}</div>}

                                    <div className="ptr-section">
                                        <h4 className="ptr-section-title"><i className="fas fa-building" /> Company Information</h4>
                                        <div className="ptr-grid ptr-grid-2">
                                            <div className="pt-field">
                                                <label className="pt-label">Company Name *</label>
                                                <input className="pt-input" placeholder="e.g. ADNOC" value={reqForm.company_name} onChange={e => updateReqField('company_name', e.target.value)} />
                                            </div>
                                            <div className="pt-field">
                                                <label className="pt-label">Contact Person *</label>
                                                <input className="pt-input" placeholder="Full name" value={reqForm.contact_name} onChange={e => updateReqField('contact_name', e.target.value)} />
                                            </div>
                                            <div className="pt-field">
                                                <label className="pt-label">Email *</label>
                                                <input className="pt-input" type="email" placeholder="email@company.com" value={reqForm.contact_email} onChange={e => updateReqField('contact_email', e.target.value)} />
                                            </div>
                                            <div className="pt-field">
                                                <label className="pt-label">Phone</label>
                                                <input className="pt-input" placeholder="+971 ..." value={reqForm.contact_phone} onChange={e => updateReqField('contact_phone', e.target.value)} />
                                            </div>
                                            <div className="pt-field">
                                                <label className="pt-label">Country</label>
                                                <input className="pt-input" placeholder="e.g. UAE" value={reqForm.country} onChange={e => updateReqField('country', e.target.value)} />
                                            </div>
                                            <div className="pt-field">
                                                <label className="pt-label">Industry</label>
                                                <input className="pt-input" placeholder="e.g. Oil & Gas" value={reqForm.industry} onChange={e => updateReqField('industry', e.target.value)} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ptr-section">
                                        <div className="ptr-section-head">
                                            <h4 className="ptr-section-title"><i className="fas fa-users" /> Positions Required</h4>
                                            <button type="button" className="ptr-add-btn" onClick={addPosition}><i className="fas fa-plus" /> Add Position</button>
                                        </div>

                                        {reqForm.positions.map((pos, idx) => (
                                            <div className="ptr-pos-card" key={idx}>
                                                <div className="ptr-pos-header">
                                                    <span className="ptr-pos-num">Position #{idx + 1}</span>
                                                    {reqForm.positions.length > 1 && (
                                                        <button type="button" className="ptr-pos-remove" onClick={() => removePosition(idx)}><i className="fas fa-trash-alt" /></button>
                                                    )}
                                                </div>
                                                <div className="ptr-grid ptr-grid-3">
                                                    <div className="pt-field">
                                                        <label className="pt-label">Position / Job Title *</label>
                                                        <select className="pt-select" value={pos.position} onChange={e => updatePosition(idx, 'position', e.target.value)}>
                                                            <option value="">Select Position</option>
                                                            {filterOpts?.positions?.map(p => <option key={p} value={p}>{p}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Headcount *</label>
                                                        <input className="pt-input" type="number" min="1" value={pos.headcount} onChange={e => updatePosition(idx, 'headcount', e.target.value)} />
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Nationality Pref.</label>
                                                        <select className="pt-select" value={pos.nationality_preference} onChange={e => updatePosition(idx, 'nationality_preference', e.target.value)}>
                                                            <option value="">Any</option>
                                                            {filterOpts?.nationalities?.map(n => <option key={n} value={n}>{n}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Min Experience</label>
                                                        <select className="pt-select" value={pos.min_experience} onChange={e => updatePosition(idx, 'min_experience', e.target.value)}>
                                                            <option value="">Any</option>
                                                            {[0, 1, 2, 3, 5, 7, 10, 15, 20].map(y => <option key={y} value={y}>{y} yrs</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Max Experience</label>
                                                        <select className="pt-select" value={pos.max_experience} onChange={e => updatePosition(idx, 'max_experience', e.target.value)}>
                                                            <option value="">Any</option>
                                                            {[2, 5, 10, 15, 20, 25, 30].map(y => <option key={y} value={y}>{y} yrs</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Min Age</label>
                                                        <select className="pt-select" value={pos.min_age} onChange={e => updatePosition(idx, 'min_age', e.target.value)}>
                                                            <option value="">Any</option>
                                                            {[20, 25, 30, 35, 40, 45, 50].map(a => <option key={a} value={a}>{a}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Max Age</label>
                                                        <select className="pt-select" value={pos.max_age} onChange={e => updatePosition(idx, 'max_age', e.target.value)}>
                                                            <option value="">Any</option>
                                                            {[30, 35, 40, 45, 50, 55, 60, 65].map(a => <option key={a} value={a}>{a}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Certificates</label>
                                                        <input className="pt-input" placeholder="e.g. IWCF, IADC" value={pos.certificates} onChange={e => updatePosition(idx, 'certificates', e.target.value)} />
                                                    </div>
                                                    <div className="pt-field">
                                                        <label className="pt-label">Education</label>
                                                        <input className="pt-input" placeholder="e.g. BSc" value={pos.educational_degree} onChange={e => updatePosition(idx, 'educational_degree', e.target.value)} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="ptr-section">
                                        <h4 className="ptr-section-title"><i className="fas fa-sticky-note" /> Additional Notes</h4>
                                        <textarea className="pt-input ptr-textarea" rows="3" placeholder="Any additional requirements, timeline, or special instructions..." value={reqForm.notes} onChange={e => updateReqField('notes', e.target.value)} />
                                    </div>

                                    <div className="ptr-footer">
                                        <button type="button" className="pt-btn pt-btn-ghost" onClick={() => setShowRequestModal(false)}>Cancel</button>
                                        <button type="button" className="pt-btn pt-btn-primary" disabled={requestSubmitting} onClick={submitRequest}>
                                            {requestSubmitting
                                                ? <><span className="pt-spinner" style={{ width: 18, height: 18, borderWidth: 2, display: 'inline-block' }} /> Submitting...</>
                                                : <><i className="fas fa-paper-plane" /> Submit Request</>}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PetroTalent;
